<script setup lang="ts">
import { getCurrentInstance, nextTick, onMounted, ref } from 'vue';
import { createObject, deleteObject, listObjects, updateObject } from '@/base/callBackend';
import { CabOrganization, CabTheme, uuidOf } from 'common';
// noinspection ES6UnusedImports - usages in <template> are not properly detected!
import { ORGANIZATION_CHANGE_EMIT, ORGANIZATION_REMOVE_EMIT } from '@/base/cabConstants';
import InstellingStrip from '@/views/admin/InstellingStrip.vue';
import PlusButton from '@/views/icons/PlusIcon.vue';
import GeneralFilter from '@/views/general/GeneralFilter.vue';

const organizations         = ref<CabOrganization[]>([]);
const themes                = ref<CabTheme[]>([]);
const filterActive          = ref<boolean>(false);
const filteredOrganizations = ref<CabOrganization[]>([]);

onMounted(async () => {
    organizations.value = await listObjects(CabOrganization);
    themes.value        = await listObjects(CabTheme);
});

//=====================================================================================
async function onOrganizationRemove(organization: CabOrganization) {
    await deleteObject(organization);
    organizations.value = organizations.value.filter((o) => uuidOf(o) !== uuidOf(organization));
}

async function onOrganizationAdd() {
    filterActive.value    = false;
    const newOrganization = new CabOrganization();
    await createObject(newOrganization);
    organizations.value = [newOrganization, ...organizations.value];
    await nextTick();
    getOrganizationStrip(makeStripRef(newOrganization))?.firstEdit();
}

async function onOrganizationChange(organization: CabOrganization) {
    const uuid = uuidOf(organization);
    await updateObject(organization);
    organizations.value = organizations.value.map((o) =>
        uuidOf(o) === uuid ? organization : o,
    );
}

const instance = getCurrentInstance();

function getOrganizationStrip(ref: string): InstanceType<typeof InstellingStrip> | undefined {
    return (instance?.proxy?.$refs[ref] as any)?.[0];
}

function makeStripRef(x: CabOrganization) {
    return `OrganizationStrip-${x.uuid}`;
}

//=====================================================================================
</script>

<template>
    <div class="flex flex-col flex-1 mx-28 my-5">
        <div class="flex flex-row flex-1 mb-1">
            <PlusButton @click="onOrganizationAdd()"/>
            <div class="text-2xl flex-1 ml-2">
                Instellingen
            </div>
            <GeneralFilter v-model:active="filterActive"
                           v-model:cabList="organizations"
                           v-model:cabListFiltered="filteredOrganizations"
                           :id="'OrganizationList'"/>
        </div>
        <div class="flex flex-row items-center w-full text-xs text-gray-500 mx-0.5 px-1"
             v-if="filteredOrganizations.length"
        >
            <div class="w-96 px-3">naam</div>
            <div class="flex-1 px-3">thema's</div>
            <div class="w-28 mx-1 text-center">deeltijd</div>
            <div class="w-24 mx-3 text-center">API</div>
            <div class="icon-button-space"/>
        </div>
        <InstellingStrip
            v-for="organization in filteredOrganizations"
            :key="uuidOf(organization)"
            :organization="organization"
            :allThemes="themes"
            :ref="makeStripRef(organization)"
            v-on:[ORGANIZATION_REMOVE_EMIT]="onOrganizationRemove"
            v-on:[ORGANIZATION_CHANGE_EMIT]="onOrganizationChange"
        />
    </div>
</template>
